interface Props {
  value: number;
  max?: number;
  unit?: string;
  label?: string;
  color?: string;
  size?: number;
  thresholdWarn?: number;
  thresholdCrit?: number;
}

export function GaugeArc({
  value,
  max = 100,
  unit = "%",
  label,
  color = "var(--cyan)",
  size = 120,
  thresholdWarn,
  thresholdCrit,
}: Props) {
  const pct = Math.min(1, Math.max(0, value / max));
  const r = size / 2 - 10;
  const cx = size / 2;
  const cy = size / 2 + 6;
  const startA = Math.PI * 0.85;
  const endA = Math.PI * 2.15;
  const totalA = endA - startA;
  const valA = startA + pct * totalA;
  const polar = (a: number, rr: number) => [cx + Math.cos(a) * rr, cy + Math.sin(a) * rr] as const;
  const arcPath = (a0: number, a1: number, rr: number) => {
    const [x0, y0] = polar(a0, rr);
    const [x1, y1] = polar(a1, rr);
    const large = a1 - a0 > Math.PI ? 1 : 0;
    return `M${x0.toFixed(2)},${y0.toFixed(2)} A${rr},${rr} 0 ${large} 1 ${x1.toFixed(2)},${y1.toFixed(2)}`;
  };
  let arcColor = color;
  if (thresholdCrit != null && value >= thresholdCrit) arcColor = "var(--rose)";
  else if (thresholdWarn != null && value >= thresholdWarn) arcColor = "var(--amber)";
  return (
    <svg viewBox={`0 0 ${size} ${size}`} style={{ width: size, height: size, overflow: "visible" }}>
      <path d={arcPath(startA, endA, r)} fill="none" stroke="var(--bg-3)" strokeWidth="8" strokeLinecap="round" />
      <path
        d={arcPath(startA, Math.max(startA + 0.001, valA), r)}
        fill="none"
        stroke={arcColor}
        strokeWidth="8"
        strokeLinecap="round"
        style={{ filter: `drop-shadow(0 0 8px ${arcColor})`, transition: "stroke 300ms" }}
      />
      <text x={cx} y={cy + 2} fontSize="22" fill="var(--fg-0)" textAnchor="middle" fontFamily="var(--mono)" fontWeight="500">
        {value < 10 ? value.toFixed(1) : Math.round(value)}
      </text>
      <text x={cx} y={cy + 18} fontSize="10" fill="var(--fg-2)" textAnchor="middle" fontFamily="var(--mono)">
        {unit}
      </text>
      {label && (
        <text x={cx} y={cy - 22} fontSize="9" fill="var(--fg-2)" textAnchor="middle" letterSpacing="1">
          {label.toUpperCase()}
        </text>
      )}
    </svg>
  );
}
